/**
 * Middleware CSRF — Structura Food Market.
 * Aplica solo cuando la sesión viaja en cookie HttpOnly (double-submit pattern).
 * Cuando SESSION_TRANSPORT=localStorage/Bearer: CSRF no aplica (Bearer es immune a CSRF).
 *
 * Port de Booking Hub api-csrf.ts adaptado al naming SM.
 */
import { createMiddleware } from "@tanstack/react-start";
import { json } from "@/lib/sm-http";
import { validateSmCsrfForMutation } from "@/lib/sm-session-bridge";

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * Rutas que manejan su propia auth o son públicas (no necesitan CSRF check).
 * Login/register: no tienen cookie todavía. Webhooks: tienen sus propias firmas.
 * Logout NO está en la lista: el cliente ya envía clientRequestHeaders() con CSRF token.
 */
const CSRF_SKIP_PREFIXES = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/callback",
  "/api/auth/auth0/",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/webhooks/",
  "/api/billing/",
  "/api/leads",
  "/api/invitations/",
  "/api/market/",        // storefront público
  "/api/institucional/", // B2B público
  "/api/pricing",        // pricing API pública
  "/api/shipping/",      // cotización checkout storefront
  "/api/self-service/",  // wizard ContratarModal (público; visitantes sin cookie CSRF)
  "/api/pay/",           // checkout público — clientes finales sin CSRF
  "/api/display/",       // Customer Display claim/state (tablet pública sin cookie CSRF)
  "/api/signage/",       // Digital Signage claim/player (TV pública sin cookie CSRF)
  "/api/help-analytics", // beacon de telemetría (sendBeacon no puede enviar CSRF)
  "/api/client-telemetry", // beacon de telemetría UI — mismo caso que help-analytics, sin mutación de estado sensible
];

function shouldSkipCsrf(pathname: string): boolean {
  return CSRF_SKIP_PREFIXES.some((p) => pathname.startsWith(p));
}

export const apiCsrfMiddleware = createMiddleware().server(async ({ request, next }) => {
  const url = new URL(request.url);
  if (!url.pathname.startsWith("/api/")) return next();
  if (!MUTATION_METHODS.has(request.method)) return next();
  if (shouldSkipCsrf(url.pathname)) return next();

  if (!validateSmCsrfForMutation(request)) {
    return json({ error: "Token CSRF inválido o ausente" }, 403);
  }

  return next();
});
